import React, { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useParams } from "react-router-dom";

import { getLegajoById } from "../../../api/marcacionApi";
import {
  getEgresos,
  getIngresos,
  getSignosVitalesbyLegajo,
} from "../../../api/pacienteApi";
import {
  getEvolucionesbylegajo,
  getMedicacionesbyLegajo,
  getInsumoUsadobyLegajo,
} from "../../../api/EvolucionApi";

import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./FichaLegajo.scss";

import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.result)) return value.result;
  return [];
};

const firstValue = (record, fields) => {
  for (const field of fields) {
    if (record?.[field] !== undefined && record?.[field] !== null && record?.[field] !== "") {
      return record[field];
    }
  }
  return null;
};

const displayValue = (value, suffix = "") =>
  value === undefined || value === null || value === "" ? "—" : `${value}${suffix}`;

const formatDateTime = (value) => {
  if (!value) return "—";
  const text = String(value).trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (!match) return text;
  const [, year, month, day, hour, minute, second] = match;
  const date = `${day}/${month}/${year}`;
  if (!hour) return date;
  return `${date} · ${hour}:${minute}${second ? `:${second}` : ""}`;
};

const getRecordDate = (record) => firstValue(record, ["fechahora", "fechaHora", "FechaHora"]);
const getCreationDate = (record) => firstValue(record, ["fechaCreacion", "fechacreacion", "FechaCreacion"]);

function DateMetadata({ record }) {
  return (
    <dl className="legajo-date-meta">
      <div><dt>Fecha del registro</dt><dd>{formatDateTime(getRecordDate(record))}</dd></div>
      <div><dt>Creado en sistema</dt><dd>{formatDateTime(getCreationDate(record))}</dd></div>
    </dl>
  );
}

function EmptyState({ children }) {
  return <div className="legajo-empty">{children}</div>;
}

function Section({ title, count, children, className = "" }) {
  return (
    <section className={`legajo-panel ${className}`.trim()}>
      <header className="legajo-panel__header">
        <h2>{title}</h2>
        {typeof count === "number" && <span>{count}</span>}
      </header>
      <div className="legajo-panel__body">{children}</div>
    </section>
  );
}

function LoadingPanel() {
  return (
    <div className="legajo-panel legajo-loading" aria-label="Cargando datos">
      <Spinner animation="border" role="status" size="sm" />
      <span>Cargando datos…</span>
    </div>
  );
}

function Evoluciones({ evoluciones }) {
  return (
    <Section title="Evoluciones de enfermería" count={evoluciones.length} className="legajo-panel--wide">
      {evoluciones.length === 0 ? <EmptyState>No hay evoluciones asociadas a este legajo.</EmptyState> : (
        <ol className="legajo-timeline">
          {evoluciones.map((work, index) => (
            <li key={work.idevolucion || index}>
              <div className="legajo-timeline__marker" aria-hidden="true" />
              <div className="legajo-timeline__content">
                <p className="legajo-evolution-text">{displayValue(work.texto)}</p>
                <DateMetadata record={work} />
              </div>
            </li>
          ))}
        </ol>
      )}
    </Section>
  );
}

function Medicacion({ meds }) {
  return (
    <Section title="Medicaciones" count={meds.length}>
      {meds.length === 0 ? <EmptyState>No hay medicaciones registradas.</EmptyState> : (
        <ul className="legajo-list">
          {meds.map((work, index) => (
            <li key={work.idmedicacion || work.IdMedicacion || index}>
              <div className="legajo-row-title">{displayValue(work.medicacion)}</div>
              <dl className="legajo-inline-data">
                <div><dt>Dosis</dt><dd>{displayValue(work.dosis)}</dd></div>
                <div><dt>Observación</dt><dd>{displayValue(work.obs)}</dd></div>
              </dl>
              <DateMetadata record={work} />
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function Insumos({ insumos }) {
  return (
    <Section title="Insumos utilizados" count={insumos.length}>
      {insumos.length === 0 ? <EmptyState>No hay insumos registrados.</EmptyState> : (
        <ul className="legajo-list">
          {insumos.map((work, index) => (
            <li key={work.idinsumousado || work.idInsumoUsado || index}>
              <div className="legajo-row-title">{displayValue(work.insumo)}</div>
              <dl className="legajo-inline-data">
                <div><dt>Cantidad</dt><dd>{displayValue(work.cantidad)}</dd></div>
                <div><dt>Observación</dt><dd>{displayValue(work.obs)}</dd></div>
              </dl>
              <DateMetadata record={work} />
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function SignosVitales({ signosVitales }) {
  return (
    <Section title="Signos vitales" count={signosVitales.length}>
      {signosVitales.length === 0 ? <EmptyState>No hay signos vitales registrados.</EmptyState> : (
        <ul className="legajo-list">
          {signosVitales.map((work, index) => (
            <li key={work.id || work.idsignosvitales || index}>
              <div className="legajo-vitals">
                <div><span>Temperatura</span><strong>{displayValue(firstValue(work, ["temperatura", "temp"]), " °C")}</strong></div>
                <div><span>SatO₂</span><strong>{displayValue(work.sato2, "%")}</strong></div>
                <div><span>Presión arterial</span><strong>{displayValue(work.pa, " mmHg")}</strong></div>
                <div><span>Frecuencia cardiaca</span><strong>{displayValue(work.fc, " lpm")}</strong></div>
                <div><span>Frecuencia respiratoria</span><strong>{displayValue(work.fr, " rpm")}</strong></div>
                <div><span>HGT</span><strong>{displayValue(work.hgt, " mg/dL")}</strong></div>
              </div>
              <DateMetadata record={work} />
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function BalanceHidrico({ egresos, ingresos }) {
  const groups = [{ title: "Ingresos", records: ingresos }, { title: "Egresos", records: egresos }];
  return (
    <Section title="Balance hídrico" count={egresos.length + ingresos.length}>
      <div className="legajo-balance">
        {groups.map((group) => (
          <div key={group.title}>
            <h3>{group.title}</h3>
            {group.records.length === 0 ? <EmptyState>Sin registros.</EmptyState> : (
              <ul className="legajo-list legajo-list--compact">
                {group.records.map((work, index) => (
                  <li key={work.id || index}>
                    <div className="legajo-row-title">
                      <span>{displayValue(work.liq)}</span>
                      <strong className="legajo-amount">{displayValue(work.cant, " ml")}</strong>
                    </div>
                    <DateMetadata record={work} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

const isValidCoord = (value) => value !== null && value !== undefined && value !== "" && !Number.isNaN(Number(value)) && Number(value) !== 0;

function LocationPanel({ title, coordinates, emptyMessage }) {
  return (
    <Section title={title}>
      {coordinates ? (
        <div className="legajo-map">
          <MapContainer center={coordinates} zoom={15} scrollWheelZoom={false}>
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Marker position={coordinates}><Popup>{title}</Popup></Marker>
          </MapContainer>
        </div>
      ) : <EmptyState>{emptyMessage}</EmptyState>}
    </Section>
  );
}

function FichaLegajo() {
  const { id } = useParams();
  const [legajo, setLegajo] = useState(null);
  const [egresos, setEgresos] = useState([]);
  const [ingresos, setIngresos] = useState([]);
  const [medicaciones, setMedicaciones] = useState([]);
  const [insumos, setInsumos] = useState([]);
  const [evoluciones, setEvoluciones] = useState([]);
  const [signosVitales, setSignosVitales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectionLoading, setSectionLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    setSectionLoading(true);
    setLoadError("");
    try {
      const legajoResult = await getLegajoById(id);
      const currentLegajo = asArray(legajoResult)[0];
      if (!currentLegajo) throw new Error("No se encontró el legajo solicitado.");
      setLegajo(currentLegajo);
      setLoading(false);

      const requests = await Promise.allSettled([
        getEvolucionesbylegajo(id), getSignosVitalesbyLegajo(id), getEgresos(id), getIngresos(id),
        getInsumoUsadobyLegajo(id), getMedicacionesbyLegajo(id),
      ]);
      const getResult = (index) => requests[index].status === "fulfilled" ? asArray(requests[index].value) : [];
      setEvoluciones(getResult(0));
      setSignosVitales(getResult(1));
      setEgresos(getResult(2));
      setIngresos(getResult(3));
      setInsumos(getResult(4));
      setMedicaciones(getResult(5));
    } catch (error) {
      setLoadError(error.message || "No se pudo cargar el detalle de la guardia.");
      setLoading(false);
    } finally {
      setSectionLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return <main className="legajo-detail legajo-detail--centered"><Spinner animation="border" role="status" /><span>Cargando legajo…</span></main>;
  }

  if (loadError || !legajo) {
    return (
      <main className="legajo-detail">
        <section className="legajo-panel legajo-error">
          <h1>No se pudo abrir el legajo</h1><p>{loadError}</p>
          <button type="button" className="btn btn-outline-primary" onClick={loadData}>Reintentar</button>
        </section>
      </main>
    );
  }

  const confirmedValue = legajo.estaConfirmado?.data?.[0] ?? legajo.estaConfirmado;
  const isFinished = Number(confirmedValue) === 1;
  const entradaCoordinates = isValidCoord(legajo.Latitud) && isValidCoord(legajo.Longitud)
    ? { lat: Number(legajo.Latitud), lng: Number(legajo.Longitud) } : null;
  const salidaCoordinates = isValidCoord(legajo.latsalida) && isValidCoord(legajo.longsalida)
    ? { lat: Number(legajo.latsalida), lng: Number(legajo.longsalida) } : null;

  return (
    <main className="legajo-detail">
      <header className="legajo-heading">
        <div><h1>Detalle de guardia</h1><p>Legajo #{id} · {displayValue(legajo.Paciente)}</p></div>
        <span className={`legajo-status legajo-status--${isFinished ? "done" : "active"}`}>{isFinished ? "Finalizada" : "En progreso"}</span>
      </header>

      <section className="legajo-summary" aria-label="Resumen de la guardia">
        <div className="legajo-summary__identity">
          <span>Profesional</span>
          <strong>{displayValue(legajo.ProfesionalNombre || legajo.Usuario)}</strong>
          {legajo.ProfesionalNombre && legajo.Usuario ? <small>Cuenta: {legajo.Usuario}</small> : null}
        </div>
        <dl className="legajo-summary__grid">
          <div><dt>Paciente</dt><dd>{displayValue(legajo.Paciente)}</dd></div>
          <div><dt>Duración asignada</dt><dd>{displayValue(legajo.TotalHoraMaximo, " h")}</dd></div>
          <div><dt>Entrada</dt><dd>{formatDateTime(legajo.entradaoriginal || `${legajo.Fecha || ""} ${legajo.HoraEntrada || ""}`)}</dd></div>
          <div><dt>Salida</dt><dd>{formatDateTime(legajo.salidaoriginal || (legajo.FechaSalida ? `${legajo.FechaSalida} ${legajo.HoraSalida || ""}` : null))}</dd></div>
          <div><dt>Creado en sistema</dt><dd>{formatDateTime(legajo.FechaCreacion)}</dd></div>
          <div><dt>Horas extra máximas</dt><dd>{displayValue(legajo.horaextramaximo, " h")}</dd></div>
        </dl>
      </section>

      {sectionLoading ? <LoadingPanel /> : (
        <>
          <SignosVitales signosVitales={signosVitales} />
          <Evoluciones evoluciones={evoluciones} />
          <div className="legajo-layout"><Medicacion meds={medicaciones} /><Insumos insumos={insumos} /><BalanceHidrico egresos={egresos} ingresos={ingresos} /></div>
        </>
      )}

      <div className="legajo-locations">
        <LocationPanel title="Ubicación de entrada" coordinates={entradaCoordinates} emptyMessage="No hay coordenadas de entrada registradas." />
        <LocationPanel title="Ubicación de salida" coordinates={salidaCoordinates} emptyMessage="La ubicación estará disponible cuando se marque la salida." />
      </div>
    </main>
  );
}

export default FichaLegajo;
